import { create } from 'zustand';
import authService from '../services/authService';
import { buscarPermissoesUsuario } from '../services/permissoesService';

// ============================================================
// SISTEMA "LEMBRAR DE MIM" - Storage Abstraction
// ============================================================
// Se usuário marcou "Lembrar de mim": usa localStorage (persiste)
// Se NÃO marcou: usa sessionStorage (apaga ao fechar browser)
// ============================================================

// Verifica se "Lembrar de mim" está ativo
const isRememberMeEnabled = () => {
  return localStorage.getItem('rememberMe') === 'true';
};

// Retorna o storage apropriado baseado na preferência
const getStorage = () => {
  return isRememberMeEnabled() ? localStorage : sessionStorage;
};

// Salva no storage apropriado
const setStorageItem = (key, value) => {
  const storage = getStorage();
  storage.setItem(key, value);
};

// Remove de ambos os storages para garantir limpeza completa
const removeStorageItem = (key) => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

// Busca do storage apropriado, com fallback para o outro
const getStorageItem = (key) => {
  // Primeiro tenta no storage baseado na preferência
  const storage = getStorage();
  let value = storage.getItem(key);

  // Fallback: tenta no outro storage (caso tenha mudado preferência)
  if (!value) {
    const otherStorage = isRememberMeEnabled() ? sessionStorage : localStorage;
    value = otherStorage.getItem(key);
  }

  return value;
};

// Inicializar user de forma síncrona
const getUserFromStorage = () => {
  try {
    const userStr = getStorageItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

// Inicializar permissões
const getPermissoesFromStorage = () => {
  try {
    const permissoesStr = getStorageItem('permissoes');
    return permissoesStr ? JSON.parse(permissoesStr) : [];
  } catch {
    return [];
  }
};

// Inicializar token
const getTokenFromStorage = () => {
  try {
    return getStorageItem('accessToken');
  } catch {
    return null;
  }
};

const useAuthStore = create((set, get) => ({
  user: getUserFromStorage(),
  permissoes: getPermissoesFromStorage(),
  token: getTokenFromStorage(),
  // IMPORTANTE: usar verificação síncrona, não authService.isAuthenticated() que é async
  isAuthenticated: !!getTokenFromStorage(),
  deveTrocarSenha: false,
  isLoading: false,
  error: null,

  login: async (email, password, rememberMe = false) => {
    set({ isLoading: true, error: null });
    try {
      // Salvar preferência "Lembrar de mim" ANTES do login
      // Isso determina qual storage será usado para os tokens
      localStorage.setItem('rememberMe', rememberMe.toString());
      console.log('[AUTH_STORE] 🔐 Lembrar de mim:', rememberMe ? 'ATIVADO (localStorage)' : 'DESATIVADO (sessionStorage)');

      const loginResult = await authService.login({ email, password });
      const { access } = loginResult;
      const deveTrocarSenha = loginResult.deve_trocar_senha || false;
      const user = await authService.getCurrentUser();

      // Buscar permissões efetivas do usuário
      let permissoes = [];
      try {
        console.log('[AUTH_STORE] 🔍 Buscando permissões para user.id:', user.id);
        permissoes = await buscarPermissoesUsuario(user.id);
        console.log('[AUTH_STORE] ✅ Permissões carregadas:', permissoes.length, 'telas');
        console.log('[AUTH_STORE] 📊 Permissões com visivel_menu=true:',
          permissoes.filter(p => p.visivel_menu).map(p => p.tela_codigo));
        setStorageItem('permissoes', JSON.stringify(permissoes));
      } catch (permError) {
        console.error('[AUTH_STORE] ❌ Erro ao buscar permissões:', permError);
        console.error('[AUTH_STORE] ⚠️ Login continuará com permissoes=[]');
        // Continua login mesmo se falhar carregar permissões
      }

      set({ user, permissoes, token: access, isAuthenticated: true, deveTrocarSenha, isLoading: false });
      return user;
    } catch (error) {
      set({ isLoading: false, error: error.message });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
      // Limpar dados de ambos os storages
      removeStorageItem('permissoes');
      removeStorageItem('user');
      removeStorageItem('accessToken');
      localStorage.removeItem('rememberMe');
      set({ user: null, permissoes: [], token: null, isAuthenticated: false, deveTrocarSenha: false, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
    }
  },

  // N90 — Simular Permissão (portado do Thesys). MASTER passa a navegar como o
  // usuário-alvo (menu, botões e DADOS dele; multi-tenant respeitado porque o
  // token vira do alvo). Somente-leitura (o backend bloqueia escrita).
  iniciarSimulacao: async (userId) => {
    // troca o token ativo pelo do alvo (guarda o real do admin)
    const { user: alvoUser } = await authService.iniciarSimulacaoToken(userId);
    // guarda a sessão real do admin (camada do store), só na 1ª vez
    if (!getStorageItem('user_real')) {
      const ru = getStorageItem('user');
      const rp = getStorageItem('permissoes');
      if (ru) setStorageItem('user_real', ru);
      if (rp) setStorageItem('permissoes_real', rp);
    }
    // carrega as permissões efetivas DO ALVO
    let permissoes = [];
    try {
      permissoes = await buscarPermissoesUsuario(alvoUser.id);
    } catch (e) {
      console.error('[AUTH_STORE] Falha ao carregar permissões do alvo:', e);
    }
    setStorageItem('user', JSON.stringify(alvoUser));
    setStorageItem('permissoes', JSON.stringify(permissoes));
    set({ user: alvoUser, permissoes });
    // recarrega o app já como o alvo
    window.location.href = '/cloud';
  },

  encerrarSimulacao: async () => {
    await authService.encerrarSimulacaoToken();
    const ru = getStorageItem('user_real');
    const rp = getStorageItem('permissoes_real');
    if (ru) setStorageItem('user', ru);
    if (rp) setStorageItem('permissoes', rp);
    removeStorageItem('user_real');
    removeStorageItem('permissoes_real');
    window.location.href = '/cloud';
  },

  // Método para recarregar permissões (útil quando MASTER atualiza permissões)
  loadPermissoes: async () => {
    const { user } = get();
    if (!user) return;

    try {
      const permissoes = await buscarPermissoesUsuario(user.id);
      setStorageItem('permissoes', JSON.stringify(permissoes));
      set({ permissoes });
    } catch (error) {
      console.error('[AUTH_STORE] Erro ao recarregar permissões:', error);
    }
  },

  // Helper: Verifica se usuário pode ver menu
  podeVerMenu: (codigoTela) => {
    const { user, permissoes } = get();
    // MASTER sempre vê todos os menus (mesmo padrão de podeVisualizar)
    if (user?.role === 'MASTER') return true;
    console.log(`[AUTH_STORE] 🔍 podeVerMenu(${codigoTela}):`, {
      total_permissoes: permissoes.length,
      permissao_encontrada: !!permissoes.find(p => p.tela_codigo === codigoTela)
    });
    const permissao = permissoes.find(p => p.tela_codigo === codigoTela);
    const resultado = permissao ? permissao.visivel_menu : false;
    console.log(`[AUTH_STORE] ${resultado ? '✅' : '❌'} ${codigoTela}: ${resultado}`);
    return resultado;
  },

  // Helper: Verifica se usuário pode visualizar tela (usado pelo PermissionRoute)
  podeVisualizar: (codigoTela) => {
    const { user, permissoes } = get();
    // MASTER sempre tem acesso total (bypass)
    if (user?.role === 'MASTER') return true;
    const permissao = permissoes.find(p => p.tela_codigo === codigoTela);
    return permissao ? permissao.pode_visualizar : false;
  },

  // Story 2.26: wrappers para verificar permissoes CRUD por tela.
  // Usados em botoes (disabled={!podeEditar('HOSPITAIS_LIST')}) e
  // em forms (guard antes do submit como defense-in-depth).
  // MASTER sempre passa.
  podeCriar: (codigoTela) => {
    const { user, permissoes } = get();
    if (user?.role === 'MASTER') return true;
    const permissao = permissoes.find(p => p.tela_codigo === codigoTela);
    return permissao ? permissao.pode_criar === true : false;
  },

  podeEditar: (codigoTela) => {
    const { user, permissoes } = get();
    if (user?.role === 'MASTER') return true;
    const permissao = permissoes.find(p => p.tela_codigo === codigoTela);
    return permissao ? permissao.pode_editar === true : false;
  },

  podeExcluir: (codigoTela) => {
    const { user, permissoes } = get();
    if (user?.role === 'MASTER') return true;
    const permissao = permissoes.find(p => p.tela_codigo === codigoTela);
    return permissao ? permissao.pode_excluir === true : false;
  },

  podeExportar: (codigoTela) => {
    const { user, permissoes } = get();
    if (user?.role === 'MASTER') return true;
    const permissao = permissoes.find(p => p.tela_codigo === codigoTela);
    return permissao ? permissao.pode_exportar === true : false;
  },

  setDeveTrocarSenha: (value) => set({ deveTrocarSenha: value }),

  clearError: () => set({ error: null }),
}));

export default useAuthStore;
